import { HttpClient } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { createHttpParams } from '~/infrastructure/http/params';
import { augmentPaging, DataWithPagingMetadata } from '~/infrastructure/paging';
import {
    SiteTransferRequestListing,
    SiteTransferRequestSummary,
    SiteTransferRequestCreateModel,
    SiteTransferRequestUpdateModel,
    SiteTransferHistoryListing
} from '~/models/modules/Stocks/site-transfer';
import { ApiServiceBase, API_NAMES } from '~/services/api';

@Injectable({ providedIn: 'root' })
export class SiteTransfersService extends ApiServiceBase {
    constructor(injector: Injector, private http: HttpClient) {
        super(injector);
        this.setBase(API_NAMES.stocks);
    }

    getSiteTransfer(id: number): Observable<SiteTransferRequestSummary> {
        return this.http.get<SiteTransferRequestSummary>(this._url(`/${id}`));
    }

    getSiteTransfers(paramsObj?: any, model?: any): Observable<DataWithPagingMetadata<SiteTransferRequestListing>> {
        paramsObj = paramsObj || {};
        const params = createHttpParams(paramsObj);

        return this.http
            .post<SiteTransferRequestListing[]>(this._url(`/grid-data`), model, {
                params,
                observe: 'response',
            })
            .pipe(map(augmentPaging));
    }

    getSiteTransferHistory(paramsObj?: any, model?: any): Observable<DataWithPagingMetadata<SiteTransferHistoryListing>> {
        paramsObj = paramsObj || {};
        const params = createHttpParams(paramsObj);

        return this.http
            .post<SiteTransferHistoryListing[]>(this._url(`/history/grid-data`), model, {
                params,
                observe: 'response',
            })
            .pipe(map(augmentPaging));
    }

    createSiteTransfer(model: SiteTransferRequestCreateModel): Observable<any> {
        return this.http.post(this._url(), model);
    }

    updateSiteTransfer(id: number, model: SiteTransferRequestUpdateModel): Observable<any> {
        return this.http.put(this._url(`/${id}`), model);
    }

    deleteSiteTransfer(id: number): Observable<any> {
        return this.http.delete(this._url(`/${id}`));
    }

    declineSiteTransfer(id: number): Observable<any> {
        return this.http.put(this._url(`/${id}/decline`), {});
    }

    closeSiteTransfer(id: number): Observable<any> {
        return this.http.put(this._url(`/${id}/close`), {});
    }

    batchDeactivate(model: { ListOf: number[] }): Observable<any> {
        return this.http.put(this._url(`/batchDeactivate`), model);
    }

    batchReactivate(model: { ListOf: number[] }): Observable<any> {
        return this.http.put(this._url(`/batchReactivate`), model);
    }

    batchActivateDeactivate(model: any): Observable<any> {
        return this.http.put(this._url(`/batchActivateDeactivate`), model);
    }

    getPendingOutRequests(siteId?: number): Observable<SiteTransferRequestSummary[]> {
        const query = siteId ? `?siteId=${siteId}` : '';
        return this.http.get<SiteTransferRequestSummary[]>(this._url(`/pending-out${query}`));
    }

    getPendingInRequests(siteId?: number): Observable<SiteTransferRequestSummary[]> {
        const query = siteId ? `?siteId=${siteId}` : '';
        return this.http.get<SiteTransferRequestSummary[]>(this._url(`/pending-in${query}`));
    }

    private _url(v?: string): string {
        v = v || '';
        return this.url(`/SiteTransfers${v}`);
    }
}
